import { useState, useEffect } from 'preact/hooks';
import { getBillingStatus, createBuyIntent, type BillingStatus, type Brand } from '../api/billing';

/**
 * Admin Stripe test page (buy-loop Inc 4 · stripe-buy-loop-architecture.md §8-A).
 *
 * The on-demand loop test rig: a catalog of the three token-buyable brand SKUs. Each "Buy (test)"
 * button mints a signed buy-intent for that brand → redirects to the inctasoft-cf edge checkout
 * (or the Customer Portal if the admin already has a live subscription).
 *
 * When the buy-loop envs (BUY_INTENT_SECRET / BUY_LOOP_CHECKOUT_URL / STRIPE_PRICE_* /
 * STRIPE_BRAND_PRICE_TIERS) are unset, the mint fail-closes (500) — the button surfaces a clear
 * error instead of a dead redirect. That is EXPECTED until the operator wires the products + envs;
 * the live `4242…` loop-close is operator-gated.
 */

const CATALOG: Array<{ brand: Brand; label: string; blurb: string }> = [
  { brand: 'valko', label: 'Valko', blurb: 'Consumer voice AI subscription.' },
  { brand: 'protofac', label: 'Protofac', blurb: 'Developer harness subscription.' },
  { brand: 'siynergia', label: 'Siynergia', blurb: 'Enterprise team orchestration subscription.' },
];

export function BillingTestTab() {
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [busy, setBusy] = useState<Brand | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getBillingStatus()
      .then(setStatus)
      .catch(() => setError('Could not load billing status.'));
  }, []);

  async function buy(brand: Brand) {
    setBusy(brand);
    setError(null);
    try {
      const { url } = await createBuyIntent(brand, 'pro');
      window.location.href = url; // checkout redirect OR customer portal
    } catch (err) {
      setError(
        `Buy-intent for "${brand}" failed: ${err instanceof Error ? err.message : String(err)}. ` +
          'The buy-loop envs are likely not set yet (BUY_INTENT_SECRET / BUY_LOOP_CHECKOUT_URL / ' +
          'STRIPE_PRICE_* / STRIPE_BRAND_PRICE_TIERS) — this is expected until the operator wires them.',
      );
      setBusy(null);
    }
  }

  return (
    <div class="admin-content" style={{ padding: '16px' }}>
      <h3 style={{ margin: '0 0 6px', color: '#c8c8e8', fontSize: 15 }}>Stripe buy-loop — test rig</h3>
      <p style={{ color: '#5a5a7e', fontSize: 12, margin: '0 0 16px', lineHeight: 1.5 }}>
        Mints a signed buy-intent for a brand and redirects to the inctasoft-cf edge checkout (test mode).
        Pay with <code>4242 4242 4242 4242</code> to close the loop. Already subscribed → routed to the
        Stripe Customer Portal, never a second subscription.
      </p>

      {status && (
        <div
          style={{
            display: 'flex', gap: 16, flexWrap: 'wrap', padding: '10px 14px',
            background: '#1a1a2e', borderRadius: 8, marginBottom: 16, fontSize: 12, color: '#8888aa',
          }}
        >
          <span>Deployment brand: <strong style={{ color: '#c8c8e8' }}>{status.brand}</strong></span>
          <span>Your tier: <strong style={{ color: '#c8c8e8' }}>{status.tier}</strong></span>
          <span>Subscription: <strong style={{ color: status.hasSubscription ? '#4caf50' : '#f44336' }}>{status.hasSubscription ? 'active' : 'none'}</strong></span>
        </div>
      )}

      {error && (
        <div class="message error" style={{ marginBottom: 16, fontSize: 12, lineHeight: 1.5 }}>{error}</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {CATALOG.map((sku) => (
          <div
            key={sku.brand}
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 14px', background: '#1a1a2e', borderRadius: 8, gap: 12,
            }}
          >
            <div>
              <div style={{ color: '#c8c8e8', fontWeight: 600, fontSize: 14 }}>{sku.label}</div>
              <div style={{ color: '#5a5a7e', fontSize: 12 }}>{sku.blurb}</div>
            </div>
            <button
              class="settings-btn save"
              disabled={busy !== null}
              onClick={() => buy(sku.brand)}
            >
              {busy === sku.brand ? 'Redirecting…' : 'Buy (test)'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
